import { useEffect, useState } from "react";
import { Button, Form, InputGroup, Modal, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { type PaginationState, type SortingState } from "@tanstack/react-table";
import {
  api,
  type FirmwareVersionDTO,
  type FirmwareVersionsSortOptions,
} from "../../utils/api";
import { useDebounce } from "../../utils/useDebounce";
import { ErrorMessage } from "../../authorization/ErrorMessage";
import { FirmwareVersionsTable } from "../tables/FirmwareVersionsTable";
import { ForceUpdateField, isForceUpdateChecked } from "../ForceUpdateField";
import {
  hasBinFileExtension,
  BIN_FILE_EXTENSION_ERROR,
} from "../../utils/firmwareValidation";
import { toast } from "react-toastify";

type UploadedFirmwareMetadata = {
  fileName: string;
  downloadUrl: string;
  sha256: string;
  fileSize: number;
  versionName: string;
};

export default function FirmwareVersions({
  token,
  isAdmin,
}: {
  token: string;
  isAdmin: boolean;
}) {
  const [versions, setVersions] = useState<FirmwareVersionDTO[]>([]);
  const [totalVersions, setTotalVersions] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [editingVersion, setEditingVersion] =
    useState<FirmwareVersionDTO | null>(null);
  const [deletingVersion, setDeletingVersion] =
    useState<FirmwareVersionDTO | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createFormKey, setCreateFormKey] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingFirmware, setIsUploadingFirmware] = useState(false);
  const [uploadedFirmware, setUploadedFirmware] =
    useState<UploadedFirmwareMetadata | null>(null);
  const [selectedFirmwareFile, setSelectedFirmwareFile] = useState<File | null>(
    null
  );
  const [suggestedVersionCode, setSuggestedVersionCode] = useState<
    number | null
  >(null);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [dataTimestamp, setDataTimestamp] = useState(0);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = useState<SortingState>([
    { id: "createdAt", desc: true },
  ]);

  const getApiErrorMessage = (error: any, fallbackMessage: string): string => {
    if (typeof error === "string") {
      if (error.includes("413 Request Entity Too Large")) {
        return "Firmware file is too large for upload.";
      }
      return error;
    }

    const responseData = error?.response?.data;
    if (typeof responseData === "string") {
      if (responseData.includes("413 Request Entity Too Large")) {
        return "Firmware file is too large for upload.";
      }
      return responseData;
    }

    return (
      error?.error ||
      responseData?.error ||
      error?.detail ||
      responseData?.detail ||
      error?.message ||
      responseData?.message ||
      error?.title ||
      responseData?.title ||
      fallbackMessage
    );
  };

  const handleDownload = async (version: FirmwareVersionDTO) => {
    try {
      const blob = await api.downloadFirmware(token, version.fileName);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = version.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      toast.error(getApiErrorMessage(error, "Failed to download firmware"));
    }
  };

  const resetCreateForm = () => {
    setUploadedFirmware(null);
    setSelectedFirmwareFile(null);
    setIsUploadingFirmware(false);
    setSuggestedVersionCode(null);
  };

  const loadSuggestedVersionCode = async () => {
    try {
      const response = await api.getSuggestedFirmwareVersionCode(token);
      setSuggestedVersionCode(response.data.versionCode);
    } catch (error: any) {
      toast.error(
        getApiErrorMessage(
          error,
          "Failed to load suggested firmware version code"
        )
      );
    }
  };

  const parseVersionCodeFromForm = (formData: FormData): number | null => {
    const rawValue = String(formData.get("versionCode") || "").trim();
    const versionCode = Number(rawValue);
    if (!Number.isInteger(versionCode) || versionCode <= 0) {
      toast.error("Version code must be a positive integer.");
      return null;
    }
    return versionCode;
  };

  const uploadSelectedFirmware = async (file: File) => {
    const uploadFormData = new FormData();
    uploadFormData.append("file", file);
    setIsUploadingFirmware(true);
    try {
      const response = await api.uploadFirmware(token, uploadFormData);
      setUploadedFirmware({
        fileName: response.data.fileName,
        downloadUrl: response.data.downloadUrl,
        sha256: response.data.sha256,
        fileSize: response.data.fileSize,
        versionName: response.data.versionName,
      });
    } catch (error: any) {
      setUploadedFirmware(null);
      toast.error(
        getApiErrorMessage(error, "Failed to validate firmware file")
      );
      throw error;
    } finally {
      setIsUploadingFirmware(false);
    }
  };

  useEffect(() => {
    api
      .getFirmwareVersions(
        token,
        pagination.pageSize,
        pagination.pageIndex * pagination.pageSize,
        sorting.length > 0
          ? ((sorting[0]?.desc
              ? `${sorting[0].id}_desc`
              : `${sorting[0].id}_asc`) as FirmwareVersionsSortOptions)
          : undefined,
        debouncedSearchTerm || undefined
      )
      .then((body) => {
        setVersions(body.data);
        setTotalVersions(body.metadata.totalCount);
        setErrorMessage(
          !debouncedSearchTerm && body.data.length === 0
            ? "No firmware versions available."
            : ""
        );
      })
      .catch(() => {
        setErrorMessage("Failed to load firmware versions");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [token, pagination, sorting, debouncedSearchTerm, dataTimestamp]);

  if (isLoading) {
    return (
      <Spinner animation="border" role="status">
        <span className="visually-hidden">Loading...</span>
      </Spinner>
    );
  }

  return (
    <div>
      {isAdmin ? (
        <div className="mb-3 d-flex justify-content-start">
          <Button
            variant="dark"
            onClick={() => {
              resetCreateForm();
              setCreateFormKey((key) => key + 1);
              setShowCreateModal(true);
              void loadSuggestedVersionCode();
            }}
          >
            Create a new firmware version
          </Button>
        </div>
      ) : null}

      <InputGroup className="mb-3">
        <InputGroup.Text>
          <label
            htmlFor="search-firmware-versions"
            title="Search firmware versions"
          >
            <FontAwesomeIcon icon={faSearch} />
          </label>
        </InputGroup.Text>
        <Form.Control
          id="search-firmware-versions"
          type="text"
          placeholder="Search firmware versions"
          value={searchTerm}
          maxLength={255}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </InputGroup>

      {(errorMessage && <ErrorMessage message={errorMessage} />) || (
        <FirmwareVersionsTable
          versions={versions}
          totalVersions={totalVersions}
          showActions={isAdmin}
          onDownload={handleDownload}
          {...(isAdmin
            ? {
                onEdit: (version: FirmwareVersionDTO) =>
                  setEditingVersion(version),
                onDelete: (version: FirmwareVersionDTO) =>
                  setDeletingVersion(version),
                onPublish: async (version: FirmwareVersionDTO) => {
                  setIsSubmitting(true);
                  try {
                    await api.updateFirmwareVersion(token, version.id, {
                      status: "PUBLISHED",
                    });
                    toast.success("Firmware version published successfully.");
                    setDataTimestamp(Date.now());
                  } catch (error: any) {
                    toast.error(
                      getApiErrorMessage(
                        error,
                        "Failed to publish firmware version"
                      )
                    );
                  } finally {
                    setIsSubmitting(false);
                  }
                },
              }
            : {})}
          onTableChange={(nextPagination, nextSorting) => {
            setPagination(nextPagination);
            setSorting(nextSorting);
          }}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          sortBy={sorting?.[0]?.id}
          sortDescending={sorting?.[0]?.desc}
        />
      )}

      {isAdmin ? (
        <Modal
          show={showCreateModal}
          onHide={() => {
            setShowCreateModal(false);
            resetCreateForm();
            setCreateFormKey((key) => key + 1);
          }}
        >
          <Modal.Header closeButton>
            <Modal.Title>Create Firmware Version</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form
              key={createFormKey}
              onSubmit={async (event) => {
                event.preventDefault();
                const formData = new FormData(event.currentTarget);
                setIsSubmitting(true);
                try {
                  if (!uploadedFirmware) {
                    toast.error("Please select a valid firmware file.");
                    return;
                  }

                  const releaseNotes = String(
                    formData.get("releaseNotes") || ""
                  ).trim();
                  if (!releaseNotes) {
                    toast.error("Release notes are required.");
                    return;
                  }

                  const versionCode = parseVersionCodeFromForm(formData);
                  if (versionCode === null) {
                    return;
                  }

                  await api.createFirmwareVersion(token, {
                    versionName: uploadedFirmware.versionName,
                    versionCode,
                    fileName: uploadedFirmware.fileName,
                    fileSize: uploadedFirmware.fileSize,
                    forceUpdate: isForceUpdateChecked(formData),
                    status: String(
                      formData.get("status") || "DRAFT"
                    ) as FirmwareVersionDTO["status"],
                    downloadUrl: uploadedFirmware.downloadUrl,
                    releaseNotes,
                    sha256: uploadedFirmware.sha256,
                  });
                  toast.success("Firmware version created successfully.");
                  setShowCreateModal(false);
                  resetCreateForm();
                  setDataTimestamp(Date.now());
                } catch (error: any) {
                  toast.error(
                    getApiErrorMessage(
                      error,
                      "Failed to create firmware version"
                    )
                  );
                } finally {
                  setIsSubmitting(false);
                }
              }}
            >
              <Form.Group className="mb-3">
                <Form.Label>Firmware (.bin)</Form.Label>
                <Form.Control
                  name="firmwareFile"
                  type="file"
                  accept=".bin"
                  required
                  onChange={async (event) => {
                    const input = event.currentTarget as HTMLInputElement;
                    const file = input.files?.[0];
                    if (!file) {
                      setSelectedFirmwareFile(null);
                      setUploadedFirmware(null);
                      return;
                    }
                    if (!hasBinFileExtension(file.name)) {
                      toast.error(BIN_FILE_EXTENSION_ERROR);
                      input.value = "";
                      setSelectedFirmwareFile(null);
                      setUploadedFirmware(null);
                      return;
                    }
                    setSelectedFirmwareFile(file);
                    setUploadedFirmware(null);
                    try {
                      await uploadSelectedFirmware(file);
                    } catch {
                      input.value = "";
                      setSelectedFirmwareFile(null);
                    }
                  }}
                />
                {selectedFirmwareFile ? (
                  <Form.Text muted>
                    Selected: {selectedFirmwareFile.name} (
                    {Math.round(selectedFirmwareFile.size / 1024)} KB)
                  </Form.Text>
                ) : null}
                {isUploadingFirmware ? (
                  <Form.Text muted>
                    Validating firmware and extracting version...
                  </Form.Text>
                ) : null}
                <Form.Text muted>
                  Version name is auto-derived from the uploaded firmware file.
                </Form.Text>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Version Name</Form.Label>
                <Form.Control
                  value={uploadedFirmware?.versionName ?? ""}
                  readOnly
                  placeholder={
                    isUploadingFirmware
                      ? "Extracting..."
                      : "Select a firmware file"
                  }
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Version Code</Form.Label>
                <Form.Control
                  name="versionCode"
                  type="number"
                  min={1}
                  step={1}
                  required
                  defaultValue={suggestedVersionCode ?? undefined}
                  key={suggestedVersionCode ?? "pending-version-code"}
                />
                <Form.Text muted>
                  Auto-filled as the next available version code. You can edit
                  it before creating the version.
                </Form.Text>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select name="status" defaultValue="DRAFT">
                  <option value="DRAFT">DRAFT</option>
                  <option value="ARCHIVED">ARCHIVED</option>
                  <option value="PUBLISHED">PUBLISHED</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Release Notes</Form.Label>
                <Form.Control name="releaseNotes" required />
              </Form.Group>
              <ForceUpdateField productLabel="firmware" />
              <div className="d-flex justify-content-end gap-2">
                <Button
                  variant="outline-secondary"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetCreateForm();
                  }}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  variant="dark"
                  type="submit"
                  disabled={
                    isSubmitting || isUploadingFirmware || !uploadedFirmware
                  }
                >
                  {isSubmitting ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Creating...
                    </>
                  ) : (
                    "Create"
                  )}
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>
      ) : null}

      {isAdmin ? (
        <Modal show={!!editingVersion} onHide={() => setEditingVersion(null)}>
          <Modal.Header closeButton>
            <Modal.Title>Edit Firmware Version</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form
              onSubmit={async (event) => {
                event.preventDefault();
                if (!editingVersion) {
                  return;
                }

                const formData = new FormData(event.currentTarget);
                setIsSubmitting(true);
                try {
                  const versionCode = parseVersionCodeFromForm(formData);
                  if (versionCode === null) {
                    return;
                  }

                  await api.updateFirmwareVersion(token, editingVersion.id, {
                    versionCode,
                    status: String(
                      formData.get("status") || "DRAFT"
                    ) as FirmwareVersionDTO["status"],
                    releaseNotes: String(formData.get("releaseNotes") || ""),
                    forceUpdate: isForceUpdateChecked(formData),
                  });
                  toast.success("Firmware version updated successfully.");
                  setEditingVersion(null);
                  setDataTimestamp(Date.now());
                } catch (error: any) {
                  toast.error(
                    getApiErrorMessage(
                      error,
                      "Failed to update firmware version"
                    )
                  );
                } finally {
                  setIsSubmitting(false);
                }
              }}
            >
              <Form.Group className="mb-3">
                <Form.Label>Firmware file</Form.Label>
                <Form.Control value={editingVersion?.fileName ?? ""} readOnly />
                <Form.Text muted>
                  Firmware file cannot be changed after creation.
                </Form.Text>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Version Name</Form.Label>
                <Form.Control
                  value={editingVersion?.versionName ?? ""}
                  readOnly
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Version Code</Form.Label>
                <Form.Control
                  name="versionCode"
                  type="number"
                  min={1}
                  step={1}
                  required
                  defaultValue={editingVersion?.versionCode}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  name="status"
                  defaultValue={editingVersion?.status ?? "DRAFT"}
                >
                  <option value="DRAFT">DRAFT</option>
                  <option value="ARCHIVED">ARCHIVED</option>
                  <option value="PUBLISHED">PUBLISHED</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Release Notes</Form.Label>
                <Form.Control
                  name="releaseNotes"
                  defaultValue={editingVersion?.releaseNotes}
                  required
                />
              </Form.Group>
              <ForceUpdateField
                productLabel="firmware"
                defaultChecked={editingVersion?.forceUpdate ?? false}
              />
              <Form.Group className="mb-3">
                <Form.Label>SHA256</Form.Label>
                <Form.Control value={editingVersion?.sha256 ?? ""} readOnly />
              </Form.Group>
              <div className="d-flex justify-content-end gap-2">
                <Button
                  variant="outline-secondary"
                  onClick={() => setEditingVersion(null)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button variant="dark" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Saving...
                    </>
                  ) : (
                    "Save"
                  )}
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>
      ) : null}

      {isAdmin ? (
        <Modal show={!!deletingVersion} onHide={() => setDeletingVersion(null)}>
          <Modal.Header closeButton>
            <Modal.Title>Delete Firmware Version</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Are you sure you want to delete{" "}
            <code>{deletingVersion?.versionName}</code>?
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="outline-secondary"
              onClick={() => setDeletingVersion(null)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={isSubmitting}
              onClick={async () => {
                if (!deletingVersion) {
                  return;
                }
                setIsSubmitting(true);
                try {
                  await api.deleteFirmwareVersion(token, deletingVersion.id);
                  toast.success("Firmware version deleted successfully.");
                  setDeletingVersion(null);
                  setDataTimestamp(Date.now());
                } catch (error: any) {
                  toast.error(
                    getApiErrorMessage(
                      error,
                      "Failed to delete firmware version"
                    )
                  );
                } finally {
                  setIsSubmitting(false);
                }
              }}
            >
              Delete
            </Button>
          </Modal.Footer>
        </Modal>
      ) : null}
    </div>
  );
}
